import { useGetDownloadParams } from '../util'
import { t } from '@/i18n'

interface CommandGeneratorProps {
  switchChange: string
  installStyle: string
  installStyle1: string
  versionChange1: string
  selectArch: string
  autoInstallDependencyEnv: boolean
  installData?: any // 当前选择的包信息
  enableDnatConfig?: boolean // 是否启用DNAT配置
  agentHostAndPort?: any // agent host和port信息
  onCommandGenerated: (command: string) => void
}

export const useCommandGenerator = ({
  switchChange,
  installStyle,
  installStyle1,
  versionChange1,
  selectArch,
  autoInstallDependencyEnv,
  installData,
  enableDnatConfig,
  agentHostAndPort,
  onCommandGenerated,
}: CommandGeneratorProps) => {
  const [getDownloadParams] = useGetDownloadParams()

  const generateCommand = async (values: any) => {
    try {
      // 构建请求参数
      const requestParams = buildRequestParams(values)

      // 添加调试日志
      console.log('包信息:', installData)
      console.log('请求参数:', requestParams)

      // 调用后端接口生成命令
      const response = await getDownloadParams(requestParams)

      if (response?.data?.command) {
        onCommandGenerated(response.data.command)
      } else {
        console.error('后端返回的命令为空')
      }
    } catch (error) {
      console.error('生成命令失败:', error)
    }
  }

  // 构建请求参数
  const buildRequestParams = (values: any) => {
    const requestParams: any = {
      autoInstallDependEnvs: autoInstallDependencyEnv,
      installMethod: getInstallMethod(),
      os: getOS(),
      osArchitecture: getOSArchitecture(),
    }

    // 添加代理信息
    if (isProxyMode(values)) {
      const proxyInfo: any = {
        address: getProxyAddress(values),
        protocol: getProxyProtocol(values),
      }

      // 只有当用户名不为空时才添加用户名
      const username = getProxyUsername(values)
      if (username && username.trim()) {
        proxyInfo.username = username
      }

      // 只有当密码不为空时才添加密码
      const password = getProxyPassword(values)
      if (password && password.trim()) {
        proxyInfo.password = password
      }

      requestParams.proxyInfo = proxyInfo
    }

    // 添加DNAT配置信息
    if (enableDnatConfig) {
      const dnatInfo = buildDnatInfo(values)
      if (dnatInfo && Object.keys(dnatInfo).length > 0) {
        requestParams.dnatInfo = dnatInfo
      }
    }

    return requestParams
  }

  // 获取安装方式
  const getInstallMethod = () => {
    const currentInstallStyle = switchChange === 'linux' ? installStyle1 : installStyle
    return currentInstallStyle === '1' ? 'TRADITIONAL' : 'ONE_CLICK'
  }

  // 获取操作系统
  const getOS = () => {
    const currentInstallStyle = switchChange === 'linux' ? installStyle1 : installStyle
    const isOneClickInstall = currentInstallStyle === '2'

    // 一键命令安装时，使用表单选择的系统版本
    if (isOneClickInstall) {
      if (switchChange === 'windows') {
        return 'AGENT_OS_WINDOWS_64BIT'
      } else if (switchChange === 'linux') {
        // 根据选择的包类型确定OS
        if (versionChange1?.includes('rpm')) {
          return 'AGENT_OS_LINUX_RPM'
        } else if (versionChange1?.includes('deb')) {
          return 'AGENT_OS_LINUX_DEB'
        }
        return 'AGENT_OS_LINUX_RPM' // 默认
      } else if (switchChange === 'uos') {
        return 'AGENT_OS_LINUX_RPM' // UOS使用RPM包
      }
    }

    // 安装包安装时，使用选择的包信息
    if (installData) {
      const os = installData.os
      if (os?.includes('Windows')) {
        return 'AGENT_OS_WINDOWS_64BIT'
      } else if (os === 'Linux .rpm') {
        return 'AGENT_OS_LINUX_RPM'
      } else if (os === 'Linux .deb') {
        return 'AGENT_OS_LINUX_DEB'
      } else if (os?.includes('UOS')) {
        return 'AGENT_OS_LINUX_RPM' // UOS使用RPM包
      }
    }

    // 默认逻辑
    if (switchChange === 'windows') {
      return 'AGENT_OS_WINDOWS_64BIT'
    } else if (switchChange === 'linux') {
      return 'AGENT_OS_LINUX_RPM'
    } else if (switchChange === 'uos') {
      return 'AGENT_OS_LINUX_RPM'
    }
    return 'AGENT_OS_UNKNOWN'
  }

  // 获取系统架构
  const getOSArchitecture = () => {
    const currentInstallStyle = switchChange === 'linux' ? installStyle1 : installStyle
    const isOneClickInstall = currentInstallStyle === '2'

    // 一键命令安装时，使用表单选择的系统架构
    if (isOneClickInstall) {
      return selectArch === 'X86' ? 'AGENT_OS_ARCHITECTURE_X86' : 'AGENT_OS_ARCHITECTURE_ARM'
    }

    // 安装包安装时，使用选择的包信息
    if (installData) {
      const architecture = installData.os_architecture
      if (architecture === 'ARM') {
        return 'AGENT_OS_ARCHITECTURE_ARM'
      } else if (architecture === 'X86') {
        return 'AGENT_OS_ARCHITECTURE_X86'
      }
    }

    // 默认逻辑
    return selectArch === 'X86' ? 'AGENT_OS_ARCHITECTURE_X86' : 'AGENT_OS_ARCHITECTURE_ARM'
  }

  // 判断是否为代理模式
  const isProxyMode = (values: any) => {
    if (switchChange === 'linux') {
      return values.disposition1 === t('代理')
    } else {
      return values.disposition === t('代理')
    }
  }

  // 获取代理地址
  const getProxyAddress = (values: any) => {
    if (switchChange === 'linux') {
      return values.address1
    } else {
      return values.address
    }
  }

  // 获取代理协议
  const getProxyProtocol = (values: any) => {
    const protocol = switchChange === 'linux' ? values.agreement1 : values.agreement
    switch (protocol) {
      case 'HTTP':
        return 'PROXY_PROTOCOL_HTTP'
      case 'HTTPS':
        return 'PROXY_PROTOCOL_HTTPS'
      case 'SOCKS5':
        return 'PROXY_PROTOCOL_SOCKS5'
      default:
        return 'PROXY_PROTOCOL_UNKNOWN'
    }
  }

  // 获取代理用户名
  const getProxyUsername = (values: any) => {
    if (switchChange === 'linux') {
      return values.username1 || ''
    } else {
      return values.username || ''
    }
  }

  // 获取代理密码
  const getProxyPassword = (values: any) => {
    if (switchChange === 'linux') {
      return values.password1 || ''
    } else {
      return values.password || ''
    }
  }

  // 构建DNAT配置信息
  const buildDnatInfo = (values: any) => {
    if (!agentHostAndPort) {
      return null
    }

    const dnatInfo: any = {}

    // source_host: 表单中的外部地址
    dnatInfo.source_host = values.source_host || ''

    // submit_destination_host: 表单中的平台地址，如果为空则使用接口返回的destination_host
    dnatInfo.submit_destination_host =
      values.submit_destination_host || agentHostAndPort.destination_host || ''

    // destination_host: 接口返回的destination_host（直接使用接口返回的值）
    dnatInfo.destination_host = agentHostAndPort.destination_host || ''

    // destination_domain: 接口返回的destination_domain（直接使用接口返回的值）
    dnatInfo.destination_domain = agentHostAndPort.destination_domain || ''

    // saas_source_port: 表单中的外部端口
    dnatInfo.saas_source_port =
      values.saas_source_port !== undefined && values.saas_source_port !== null
        ? values.saas_source_port
        : 0

    // saas_destination_port: 表单中的平台端口，如果为空则使用接口返回的值
    dnatInfo.saas_destination_port =
      values.saas_destination_port !== undefined && values.saas_destination_port !== null
        ? values.saas_destination_port
        : agentHostAndPort.saas_destination_port || 0

    // data_protect_http_source_port: 表单中的外部端口
    dnatInfo.data_protect_http_source_port =
      values.data_protect_http_source_port !== undefined &&
      values.data_protect_http_source_port !== null
        ? values.data_protect_http_source_port
        : 0

    // data_protect_http_destination_port: 表单中的平台端口，如果为空则使用接口返回的值
    dnatInfo.data_protect_http_destination_port =
      values.data_protect_http_destination_port !== undefined &&
      values.data_protect_http_destination_port !== null
        ? values.data_protect_http_destination_port
        : agentHostAndPort.data_protect_http_destination_port || 0

    // data_protect_https_source_port: 表单中的外部端口
    dnatInfo.data_protect_https_source_port =
      values.data_protect_https_source_port !== undefined &&
      values.data_protect_https_source_port !== null
        ? values.data_protect_https_source_port
        : 0

    // data_protect_https_destination_port: 表单中的平台端口，如果为空则使用接口返回的值
    dnatInfo.data_protect_https_destination_port =
      values.data_protect_https_destination_port !== undefined &&
      values.data_protect_https_destination_port !== null
        ? values.data_protect_https_destination_port
        : agentHostAndPort.data_protect_https_destination_port || 0

    // data_protect_git_source_port: 表单中的外部端口
    dnatInfo.data_protect_git_source_port =
      values.data_protect_git_source_port !== undefined &&
      values.data_protect_git_source_port !== null
        ? values.data_protect_git_source_port
        : 0

    // data_protect_git_destination_port: 表单中的平台端口，如果为空则使用接口返回的值
    dnatInfo.data_protect_git_destination_port =
      values.data_protect_git_destination_port !== undefined &&
      values.data_protect_git_destination_port !== null
        ? values.data_protect_git_destination_port
        : agentHostAndPort.data_protect_git_destination_port || 0

    return dnatInfo
  }

  return { generateCommand }
}
